import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Footer } from "../components/Footer";
import { usePlatformAuth } from "../context/platform-auth";
import { useFetch } from "../hooks/useFetch";

type WorkspaceItem = {
	id: string;
	name: string;
	slug: string;
	role: string;
	createdAt: string;
};

async function enterWorkspace(slug: string): Promise<boolean> {
	const res = await fetch(`/api/workspaces/${slug}/enter`, {
		method: "POST",
		credentials: "include",
		headers: { Origin: window.location.origin },
	});
	return res.ok;
}

export function WorkspacesPage() {
	const { user, signOut } = usePlatformAuth();
	const navigate = useNavigate();
	const { data, loading, error } = useFetch<{
		workspaces: WorkspaceItem[];
	}>("/api/workspaces");
	const autoEnterRef = useRef(false);
	const [entering, setEntering] = useState<string | null>(null);

	async function handleSignOut() {
		await signOut();
		navigate("/sign-in", { replace: true });
	}

	async function handleOpen(slug: string) {
		setEntering(slug);
		const ok = await enterWorkspace(slug);
		if (ok) {
			window.location.href = `/${slug}/`;
		} else {
			setEntering(null);
		}
	}

	// Auto-enter for single workspace
	useEffect(() => {
		if (!data || data.workspaces.length !== 1 || autoEnterRef.current) return;
		autoEnterRef.current = true;
		const ws = data.workspaces[0];
		enterWorkspace(ws.slug).then((ok) => {
			if (ok) {
				window.location.href = `/${ws.slug}/`;
			}
		});
	}, [data]);

	if (loading) return null;

	if (error) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
					<span className="text-lg font-semibold text-gray-900">Apsu</span>
					<button
						type="button"
						onClick={handleSignOut}
						className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer"
					>
						Sign Out
					</button>
				</header>
				<main className="flex-1 flex items-center justify-center">
					<p className="text-red-600">{error}</p>
				</main>
				<Footer />
			</div>
		);
	}

	const workspaces = data?.workspaces ?? [];

	// Auto-redirect for single workspace — show nothing while entering
	if (workspaces.length === 1) return null;

	// Empty state
	if (workspaces.length === 0) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
					<span className="text-lg font-semibold text-gray-900">Apsu</span>
					<button
						type="button"
						onClick={handleSignOut}
						className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer"
					>
						Sign Out
					</button>
				</header>
				<main className="flex-1 flex items-center justify-center">
					<div className="text-center">
						<h1 className="text-2xl font-bold text-gray-900 mb-2">
							Welcome{user?.name ? `, ${user.name}` : ""}
						</h1>
						<p className="text-gray-600 mb-6">
							You don't have any workspaces yet
						</p>
						<a
							href="/workspaces/new"
							className="inline-block rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
						>
							Create workspace
						</a>
						<p className="text-sm text-gray-500 mt-4">
							Or ask a workspace owner to invite you.
						</p>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	// Picker: 2+ workspaces
	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
				<span className="text-lg font-semibold text-gray-900">Apsu</span>
				<div className="flex items-center gap-4">
					<a
						href="/workspaces/new"
						className="text-sm text-blue-600 hover:text-blue-800"
					>
						Create new workspace
					</a>
					<button
						type="button"
						onClick={handleSignOut}
						className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer"
					>
						Sign Out
					</button>
				</div>
			</header>
			<main className="flex-1 p-8">
				<div className="max-w-2xl mx-auto">
					<h1 className="text-2xl font-bold text-gray-900 mb-6">
						Your workspaces
					</h1>
					<div className="grid gap-4">
						{workspaces.map((ws) => (
							<div
								key={ws.id}
								className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between"
							>
								<div>
									<h2 className="text-lg font-medium text-gray-900">
										{ws.name}
									</h2>
									<p className="text-sm text-gray-500">
										{ws.slug} &middot;{" "}
										<span className="capitalize">{ws.role}</span>
									</p>
								</div>
								<button
									type="button"
									onClick={() => handleOpen(ws.slug)}
									disabled={entering !== null}
									className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
								>
									{entering === ws.slug ? "Opening…" : "Open"}
								</button>
							</div>
						))}
					</div>
				</div>
			</main>
			<Footer />
		</div>
	);
}
